import { memo, useCallback } from "react"
import { z } from "zod"

import type { Workspace } from "@productlane/zero"
import { cn, scale } from "@productlane/lib/src/browser"
import {
	Form,
	FormInput,
	SubmitButton,
	Switch,
	useZodForm,
} from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { useRequiredPlans } from "@/lib/plans"
import { mutateOk, useZero } from "@/lib/zero"
import { AnimateChangeInHeight } from "../animate-change-in-height"

export const GoogleAnalyticsSettings = memo(
	({ workspace }: { workspace: Workspace }) => {
		const zero = useZero()
		const { passes: hasRequiredPlan } = useRequiredPlans(scale.andUp)

		const GoogleTagIdForm = useZodForm({
			schema: z.object({
				googleTagId: z.string().regex(/^G-[A-Z0-9]{1,20}$/, {
					message:
						"Google Tag ID must start with 'G-' and contain only uppercase letters and numbers",
				}),
			}),
			defaultValues: {
				googleTagId: workspace?.googleTagId ?? "",
			},
		})

		const updateIsGoogleAnalyticsEnabled = useCallback(
			async (isGoogleAnalyticsEnabled: boolean) => {
				if (workspace) {
					await mutateOk(
						zero.mutate(
							mutators.workspaces.update({
								id: workspace.id,
								isGoogleAnalyticsEnabled,
							}),
						),
					)
					analytics.track("update-google-analytics-enabled", {
						isGoogleAnalyticsEnabled,
						popover: true,
					})
				}
			},
			[workspace, zero],
		)

		const updateGoogleTagId = useCallback(
			async (googleTagId: string | null) => {
				if (workspace) {
					await mutateOk(
						zero.mutate(
							mutators.workspaces.update({
								id: workspace.id,
								googleTagId,
							}),
						),
					)
					analytics.track("update-google-tag-id", {
						googleTagId,
						popover: true,
					})
				}
			},
			[workspace, zero],
		)

		if (!workspace) return null

		return (
			// AnimateChangeInHeight sets an explicit height, so a card's row padding goes on this wrapper.
			<div>
				<AnimateChangeInHeight className="flex flex-col gap-2 pr-1">
					<div
						className={cn("flex items-center justify-between", {
							"flex-row": hasRequiredPlan,
						})}
					>
						<div className="flex flex-row items-center gap-4">
							<div className="flex flex-1 flex-col gap-1 truncate">
								<div className="text-sm font-medium">Google Analytics</div>
								<div className="text-xs font-medium text-secondary">
									Enable Google Analytics for your portal.
								</div>
							</div>
						</div>
						<Switch
							name="google"
							label={null}
							checked={workspace.isGoogleAnalyticsEnabled}
							onCheckedChange={async (value) => {
								await updateIsGoogleAnalyticsEnabled(value)
								analytics.track("enable-google-analytics", {
									value,
									popover: true,
								})
							}}
						/>
					</div>

					{workspace.isGoogleAnalyticsEnabled && (
						<div className="mb-2 mt-4">
							<div className="flex flex-1 flex-col gap-1 truncate">
								<div className="text-sm font-medium">Google tag ID</div>
								<div className="text-xs font-medium text-secondary">
									Enter your Google tag ID to activate Google Analytics for your
									portal.
								</div>
							</div>
							<Form
								form={GoogleTagIdForm}
								onSubmit={async ({ googleTagId }) => {
									await updateGoogleTagId(googleTagId)
									toast.success({ title: "Google tag ID updated" })
								}}
							>
								<div className="mt-2 flex items-start gap-2">
									<div className="flex-1">
										<FormInput
											type="text"
											placeholder="G-XXXXXXXXX"
											name="googleTagId"
										/>
									</div>
									<SubmitButton>Save</SubmitButton>
								</div>
							</Form>
						</div>
					)}
				</AnimateChangeInHeight>
			</div>
		)
	},
)
